import React, { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  IconButton, InputBase, Avatar, Badge, Toolbar, AppBar, Tooltip,
  Paper, useMediaQuery, BottomNavigation, BottomNavigationAction,
  Menu, MenuItem, Button, Divider,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import HomeIcon from '@mui/icons-material/Home'
import MapIcon from '@mui/icons-material/Map'
import FavoriteIcon from '@mui/icons-material/Favorite'
import GroupsIcon from '@mui/icons-material/Groups'
import PlaceIcon from '@mui/icons-material/Place'
import BusinessIcon from '@mui/icons-material/Business'
import NotesIcon from '@mui/icons-material/Notes'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import SettingsIcon from '@mui/icons-material/Settings'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined'
import SearchIcon from '@mui/icons-material/Search'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import MenuIcon from '@mui/icons-material/Menu'
import BrandLogo from './BrandLogo'

interface Props {
  children: React.ReactNode
}

interface CurrentUser {
  id: number
  slug: string
  email: string
  name: string
  avatar_url: string | null
  admin: boolean
}

interface SharedProps {
  current_user: CurrentUser | null
  flash?: { notice?: string | null; alert?: string | null }
}

interface NavItem {
  label: string
  href: string
  icon: React.ReactElement
  disabled?: boolean
  matches: (path: string) => boolean
}

const BASE_NAV_ITEMS: NavItem[] = [
  { label: 'Home', href: '/', icon: <HomeIcon />, matches: (p) => p === '/' },
  { label: 'Map', href: '/locations', icon: <MapIcon />, matches: (p) => p.startsWith('/locations') },
  { label: 'Saved', href: '#', icon: <FavoriteIcon />, disabled: true, matches: () => false },
  { label: 'Groups', href: '/groups', icon: <GroupsIcon />, matches: (p) => p.startsWith('/groups') },
  { label: 'Locations', href: '/locations', icon: <PlaceIcon />, matches: (p) => p.startsWith('/locations') },
  { label: 'Companies', href: '/companies', icon: <BusinessIcon />, matches: (p) => p.startsWith('/companies') },
  { label: 'Notes', href: '#', icon: <NotesIcon />, disabled: true, matches: () => false },
  { label: 'Settings', href: '#', icon: <SettingsIcon />, disabled: true, matches: () => false },
  { label: 'Help', href: '#', icon: <HelpOutlineIcon />, disabled: true, matches: () => false },
]

const ADMIN_NAV_ITEM: NavItem = {
  label: 'Suggestions',
  href: '/group_location_suggestions',
  icon: <PlaylistAddCheckIcon />,
  matches: (p) => p.startsWith('/group_location_suggestions'),
}

// Admins get an extra entry linking to the location-suggestion moderation
// portal, inserted just after the content-management items.
function navItemsFor(isAdmin: boolean): NavItem[] {
  if (!isAdmin) return BASE_NAV_ITEMS
  const items = [...BASE_NAV_ITEMS]
  const companiesIdx = items.findIndex((i) => i.label === 'Companies')
  items.splice(companiesIdx + 1, 0, ADMIN_NAV_ITEM)
  return items
}

const DRAWER_WIDTH = 232

function getCurrentPath(): string {
  if (typeof window === 'undefined') return '/'
  return window.location.pathname || '/'
}

function SidebarContent({ currentPath, items, onNavigate }: { currentPath: string; items: NavItem[]; onNavigate?: () => void }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
      <Box sx={{ px: 1, pt: 1, pb: 3 }}>
        <BrandLogo />
      </Box>
      <List disablePadding sx={{ flexGrow: 1 }}>
        {items.map((item) => {
          const active = item.matches(currentPath) && !item.disabled
          const buttonSx = {
            borderRadius: 999,
            mb: 0.75,
            px: 2,
            py: 1.1,
            color: active ? 'primary.main' : 'text.secondary',
            backgroundColor: active ? 'rgba(123, 91, 214,0.12)' : 'transparent',
            fontWeight: active ? 700 : 500,
            '&:hover': {
              backgroundColor: active ? 'rgba(123, 91, 214,0.18)' : 'rgba(123, 91, 214,0.06)',
              color: 'primary.main',
            },
            '& .MuiListItemIcon-root': {
              minWidth: 36,
              color: active ? 'primary.main' : 'inherit',
            },
          }

          if (item.disabled) {
            return (
              <Tooltip key={item.label} title="Coming soon" placement="right">
                <span>
                  <ListItemButton disabled sx={buttonSx}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 'inherit', fontSize: '0.92rem' }} />
                  </ListItemButton>
                </span>
              </Tooltip>
            )
          }

          return (
            <ListItemButton
              key={item.label}
              component={Link}
              href={item.href}
              onClick={onNavigate}
              sx={buttonSx}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 'inherit', fontSize: '0.92rem' }} />
            </ListItemButton>
          )
        })}
      </List>
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 3,
          backgroundColor: 'rgba(123, 91, 214,0.08)',
          color: 'text.secondary',
          fontSize: '0.78rem',
          lineHeight: 1.5,
        }}
      >
        <Box sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>Plan your visit</Box>
        Save places and create your K-pop journey.
      </Paper>
    </Box>
  )
}

export default function Layout({ children }: Props) {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [mobileOpen, setMobileOpen] = useState(false)
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<HTMLElement | null>(null)
  const currentPath = getCurrentPath()

  const page = usePage<SharedProps>()
  const currentUser = page.props.current_user
  const navItems = navItemsFor(Boolean(currentUser?.admin))
  const initial = currentUser ? (currentUser.name || currentUser.email).charAt(0).toUpperCase() : 'K'

  const handleDrawerToggle = () => setMobileOpen((v) => !v)
  const handleProfileMenuOpen = (e: React.MouseEvent<HTMLElement>) => setProfileMenuAnchor(e.currentTarget)
  const handleProfileMenuClose = () => setProfileMenuAnchor(null)
  const handleSignOut = () => {
    handleProfileMenuClose()
    router.delete('/users/sign_out')
  }

  const topBar = (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: 'background.default',
        color: 'text.primary',
        backdropFilter: 'blur(8px)',
        zIndex: theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: 72 }, px: { xs: 2, md: 4 }, gap: 2 }}>
        {!isDesktop && (
          <IconButton edge="start" onClick={handleDrawerToggle} aria-label="open navigation">
            <MenuIcon />
          </IconButton>
        )}
        {!isDesktop && <Box sx={{ flexShrink: 0 }}><BrandLogo size="sm" /></Box>}
        <Paper
          elevation={0}
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 0.5,
            borderRadius: 999,
            backgroundColor: '#fff',
            boxShadow: '0 1px 6px rgba(123, 91, 214,0.08)',
            maxWidth: 560,
            mx: { md: 2 },
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 1 }} />
          <InputBase
            placeholder="Search places, groups, cafes…"
            inputProps={{ 'aria-label': 'global search' }}
            sx={{ flexGrow: 1, fontSize: '0.92rem' }}
          />
        </Paper>
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
        {currentUser ? (
          <>
            <Tooltip title="Notifications">
              <IconButton aria-label="notifications">
                <Badge color="primary" variant="dot">
                  <NotificationsNoneIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title={currentUser.name}>
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{ p: 0 }}
                aria-label="open profile menu"
              >
                <Avatar
                  alt={currentUser.name}
                  src={currentUser.avatar_url || undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: 'primary.main',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                  }}
                >
                  {initial}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={profileMenuAnchor}
              open={Boolean(profileMenuAnchor)}
              onClose={handleProfileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                component={Link}
                href={`/users/${currentUser.slug || currentUser.id}`}
                onClick={handleProfileMenuClose}
              >
                My profile
              </MenuItem>
              <MenuItem
                component={Link}
                href="/users/edit"
                onClick={handleProfileMenuClose}
              >
                Edit profile
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button component={Link} href="/users/sign_in" variant="text" color="primary">
              Sign in
            </Button>
            <Button component={Link} href="/users/sign_up" variant="contained" color="primary">
              Sign up
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Desktop sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: 'background.default',
            borderRight: 'none',
            p: 1.5,
          },
        }}
        open
      >
        <Paper
          elevation={0}
          sx={{
            height: 'calc(100vh - 24px)',
            position: 'sticky',
            top: 12,
            borderRadius: 4,
            backgroundColor: '#fff',
            boxShadow: '0 2px 12px rgba(123, 91, 214,0.08)',
          }}
        >
          <SidebarContent currentPath={currentPath} items={navItems} />
        </Paper>
      </Drawer>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            backgroundColor: 'background.default',
            borderRight: 'none',
            p: 1.5,
          },
        }}
      >
        <Paper elevation={0} sx={{ height: '100%', borderRadius: 4, boxShadow: '0 2px 12px rgba(123, 91, 214,0.08)' }}>
          <SidebarContent currentPath={currentPath} items={navItems} onNavigate={() => setMobileOpen(false)} />
        </Paper>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          pb: { xs: 9, md: 0 },
        }}
      >
        {topBar}
        <Box sx={{ flexGrow: 1 }}>{children}</Box>
      </Box>

      {/* Mobile bottom nav */}
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          borderTop: '1px solid rgba(123, 91, 214,0.18)',
          backgroundColor: '#fff',
          borderRadius: 0,
        }}
      >
        <BottomNavigation
          showLabels
          value={navItems.findIndex((i) => i.matches(currentPath) && !i.disabled)}
          sx={{
            '& .Mui-selected': { color: 'primary.main' },
            backgroundColor: 'transparent',
          }}
        >
          <BottomNavigationAction component={Link} href="/" label="Home" icon={<HomeIcon />} />
          <BottomNavigationAction component={Link} href="/locations" label="Map" icon={<MapIcon />} />
          <BottomNavigationAction component={Link} href="/groups" label="Groups" icon={<GroupsIcon />} />
        </BottomNavigation>
      </Paper>
    </Box>
  )
}
